<template>
  <Transition name="fade" mode="in-out">
    <!-- BOARDS PUBLIC VIEW (Authorized) -->
    <div v-if="isAuthorized" class="w-full h-full  relative bg-none">
      <!-- Whitelabel -->
      <transition name="fadeHeader" mode="out-in">
        <WhiteLabelHeader
          v-if="lensTeam !== null"
          hide-dupe-opt
          :board-team="lensTeam"
          :shared-by="sharedByUser"
          share-type="report"
        />
        <WhiteLabelHeader
          v-else
          :board-team="lensTeam"
          hide-dupe-opt
          :shared-by="sharedByUser"
          share-type="report"
          :show-default="true"
        />
      </transition>
      <!-- Note @sam: the width excess is used for comments -->
      <MainLayout scroll-toolbar bg-color="white" aditional-classes="ml-4" no-content-padding
      style="height: calc(100vh - 88px); width: calc(100vw - 30px); z-index: 99; position: relative;">
        <!-- Toolbar -->
        <template #toolbar>
          <div class="flex items-center justify-between p-5">
            <div class="flex items-center gap-2">
              <ReportIcon class="text-icon-muted" />
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ sharedReport?.name }}
              </BaseText>
              <BaseText size="sm" class="text-text-normal">
                {{ sharedReport?.description }}
              </BaseText>
            </div>
            <!-- TODO @Sam: date -->
            <div class="flex items-center gap-1.5">
              <CalendarIcon class="text-icon-muted" />
              <BaseText type="label" size="sm" class="text-text-muted">
                {{ dateRangeLabel }}
              </BaseText>
            </div>
          </div>
        </template>
        <!-- Content -->
        <template #content>
          <div class="flex flex-col gap-8 px-3">
            <LensReportGraph
              is-public
              :public-series-data="sharedReport?.timeseriesSnapshot"
              :graph-type="sharedReport?.graph"
              :data-items="graphDataItems"
              :data-summary="graphDataSummary"
              :result-count="tableResults?.ad_groups?.length"
              :selected-kpis="selectedColumns"
            />
            <Transition name="tableFade" mode="out-in">
              <div v-if="loading.adGroups || tableResults?.ad_groups?.length === 0"
              class="grid grid-cols-12 grid-rows-6 gap-0 border relative border-neutral-50 mr-3 mt-3 rounded-lg">
                <!-- Table Loading State -->
                <div v-if="loading.adGroups" class="absolute w-full h-full flex flex-col items-center justify-center gap-1 text-center">
                  <div>
                    <BaseLoadingSpinnerCircle small :width="24" :height="24" class="text-text-muted" />
                  </div>
                  <BaseText type="label" size="sm" class="text-text-muted">
                    Loading Data...
                  </BaseText>
                  <BaseText size="sm" class="text-text-normal w-64">
                    One moment while we load the data. This might take up to a minute.
                  </BaseText>
                </div>
                <!-- No Data State -->
                <div v-else-if="tableResults?.ad_groups?.length === 0" class="absolute w-full h-full flex flex-col items-center justify-center gap-1 text-center">
                  <div>
                    <WarningIcon class="text-icon-normal" />
                  </div>
                  <BaseText type="label" size="sm" class="text-text-muted">
                    No data found
                  </BaseText>
                  <BaseText size="sm" class="text-text-normal w-64">
                    This share report does not have any data
                  </BaseText>
                </div>
                <!-- Grid background -->
                <div v-for="index in 36" :key="index" :class="{ 'border-b': index <= 30, 'border-r': index % 30 !== 0 }"
                class="border-neutral-50 h-20 col-span-2" />
              </div>
              <!-- REPORT TABLE CONTENT -->
              <LensPublicMetricTable
                v-else-if="tableResults?.ad_groups?.length > 0"
                :grouped-by="'ad_name'"
                :loading="loading.adGroups"

                :graph-info="graphInfo"
                :selected-columns.sync="selectedColumns"
                :selected-rows.sync="selectedRows"

                :table-columns="sharedReport.tableColumns"
                :current-sort="sharedReport.sort"
                :table-config="sharedReport.tableConfig"
                :max-row-count="tableResults?.ad_groups?.length"
                :summary-stats="sharedReport?.summaryStats"
                :net-results="netResults"
                :data="tableResults"

                :lens-currency="lensCurrency"

                @sort="sortTableResults"
                @pin="pinColumn"
              />
            </Transition>
          </div>
        </template>
      </MainLayout>
    </div>
    <div v-else class="relative bg-none" style="height: 100vh; width: 100%">
      <!-- BOARDS PUBLIC VIEW (Password Protected Guard) -->
      <div v-if="!showNoReport" class="flex flex-col gap-5 items-center justify-center w-full h-full relative z-10">
        <div v-if="!loading.auth" class="flex items-center gap-3">
          <img :src="lensTeam?.avatar || fallbackAvatar" class="rounded-md w-8 h-8">
          <BaseText type="label" size="lg" class="text-white">
            {{ lensTeam?.whiteLabel?.companyName }}
          </BaseText>
        </div>
        <ShareReportPasswordBlock
          :shared-by="sharedByUser"
          :page-loading="loading.auth"
          :loading="loading.unlock"
          @unlock="attemptUnlock"
        />
      </div>
      <!-- No Report Found State -->
      <div v-else class="flex flex-col gap-5 items-center justify-center w-full h-full relative z-10 text-white">
        <BaseText type="display">
          404
        </BaseText>
        <BaseText size="sm" class="text-neutral-alpha-600">
          Report is unpublished or doesn't exist
        </BaseText>
      </div>
      <!-- TODO @Sam: update gradient -->
      <div class="absolute top-0 right-0 w-full h-full select-none">
        <div class="w-full h-full relative  overflow-x-hidden z-0">
          <WhiteLabelGradient :fill="lensTeam?.whiteLabel?.companyColor" />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import LensAPI from '@/api/lens'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import FirebaseAPI from '@/api/firebase'
import { parseDateRangeFromQuery } from '../../utils/lens/filters/filterQueryUtils'
import { reverseProviderKeyMap } from '@/utils/lens/dataSourceUtils'

// Components
import MainLayout from '../../layouts/MainLayout'
import WhiteLabelHeader from '../../components/account/whiteLabel/WhiteLabelHeader.vue'
import ReportIcon from '../../components/globals/Icons/LensIcons/ReportIcon.vue'
import LensPublicMetricTable from '../../components/lens/table/LensPublicMetricTable.vue'
import LensReportGraph from '../../components/lens/graphs/LensReportGraph.vue'
import WhiteLabelGradient from '../../components/account/whiteLabel/WhiteLabelGradient.vue'
import ShareReportPasswordBlock from '../../components/lens/report/share/ShareReportPasswordBlock.vue'

// Icons
import CalendarIcon from '../../components/globals/Icons/CalendarIcon.vue'
export default {
  name: 'LensReportPublicView',
  components: {
    MainLayout,
    WhiteLabelHeader,
    LensPublicMetricTable,
    LensReportGraph,
    WhiteLabelGradient,
    ShareReportPasswordBlock,
    // Icons
    CalendarIcon,
    ReportIcon
  },
  data () {
    return {
      shareReportId: null,

      lensTeam: null,
      fallbackAvatar: require('../../assets/images/defaultProfileImage.webp'),
      showNoReport: false,
      isRollingReport: false,

      loading: {
        adGroups: true,
        unlock: false,
        auth: false
      },
      dateRange: {
        start: null,
        end: null,
        isRelativeToToday: false
      },

      isAuthorized: false,

      sharedByUser: null,
      sharedReport: null,
      tableResults: [],
      selectedRows: [],
      selectedColumns: [],
      netResults: {},
      lensCurrency: 'USD'
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricsPlatform']),
    dateRangeLabel () {
      if (this.isRollingReport) {
        return this.sharedReport?.rollingDateRange
          ?.split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }

      const { start, end } = this.sharedReport?.staticDateRange || {}
      if (!start || !end) {
        return 'All Time'
      } else {
        const options = { year: 'numeric', month: 'short', day: '2-digit' }
        const startDate = new Date(start).toLocaleDateString('en-US', options)
        const endDate = new Date(end).toLocaleDateString('en-US', options)
        return `${startDate} to ${endDate}`
      }
    },
    graphDataItems () {
      return this.tableResults?.ad_groups?.length > 0 ? this.selectedRows : []
    },
    graphDataSummary () {
      return {
        ...this.sharedReport?.summaryStats,
        startDate: this.sharedReport?.dateRange?.start || this.dateRange?.start,
        endDate: this.sharedReport?.dateRange?.end || this.dateRange?.end,
        currency: this.lensCurrency
      }
    },
    graphInfo () {
      return {
        selectedGraph: this.sharedReport?.graph,
        data: {
          bar: {
            selected: this.sharedReport?.selected_columns?.length || 0,
            total: 4
          },
          line: {
            selected: this.sharedReport?.selected_columns?.length || 0,
            total: 4
          },
          grid: {
            selected: this.sharedReport?.selected_columns?.length || 0,
            total: 12
          }
        }
      }
    }
  },
  async mounted () {
    this.loading.adGroups = true
    this.loading.auth = true
    this.shareReportId = this.$route.params.reportId
    // Fetch only public data & prompt for password if required
    const data = await this.fetchPublicSharedReport(this.shareReportId)

    // Set the metrics platform based on the provider returned by the share report
    this.setMetricsPlatform(reverseProviderKeyMap[data.provider])

    if (!data.created_by) {
      this.showNoReport = true
      this.loading.adGroups = false
      return
    }
    this.sharedByUser = await FirebaseAPI.Users.getById(data.created_by)
    if (data?.team_id) {
      this.lensTeam = await FirebaseAPI.Teams.getByID(data.team_id)
    }
    this.isAuthorized = !data.access_password
    this.loading.auth = false
    await this.fetchSharedReport(this.shareReportId)
  },
  methods: {
    ...mapActions('LensModule', ['updateColorFormat', 'updateRowColor']),
    ...mapActions('MetricsModule', ['setMetricsPlatform']),
    ...mapMutations('MetricsModule', ['SET_METRIC_LOOKUP']),

    // ============================================================================ \\
    // ========================== DATA FETCH METHODS ============================== \\
    // ============================================================================ \\

    // Fetch the shared report's publicly avaiable data (e.g., name, description, etc. - no password required)
    async fetchPublicSharedReport (shareId) {
      try {
        const { data } = await LensAPI.LensReport.getPublicShareById(shareId)
        return data
      } catch (e) {
        console.error(e)
        this.$showAlert({ type: 'error', message: 'Internal server error' })
      }
    },

    // If the report is not password protected, we can fetch the full report data directly
    async fetchSharedReport (shareId) {
      try {
        const res = await LensAPI.LensReport.getShareById(shareId)
        const { query, ...shareData } = res.data || {}
        this.isAuthorized = true
        this.setShareReport(query, shareData)
        this.loading.adGroups = false
      } catch (e) {
        console.error(e)
        // The server returns a 403 if the report is password protected
        if (e.response && e.response.status === 403) {
          this.isAuthorized = false
          this.$showAlert({ type: 'info', message: 'Password required' })
        } else {
          this.$showAlert({ type: 'error', message: 'Internal server error' })
          this.loading.adGroups = false
        }
      }
    },

    // Otherwise, require the user to enter the password and use it to fetch the full report data
    async attemptUnlock (password) {
      this.loading.unlock = true
      try {
        const res = await LensAPI.LensReport.validateSharePassword(this.shareReportId, password)
        this.isAuthorized = true
        const { query, ...shareData } = res.data || {}
        this.setShareReport(query, shareData)
        this.loading.adGroups = false
      } catch (e) {
        this.$showAlert({ type: 'error', message: 'Incorrect password' })
      } finally {
        this.loading.unlock = false
      }
    },

    // ============================================================================ \\
    // ========================== STATE INIT METHODS ============================== \\
    // ============================================================================ \\

    // Determine which state init function to use based on whether the share report uses the legacy format
    setShareReport (query, shareData) {
      // Legacy reports dont have query_results data (as they store the data in the query payload)
      const hasResults = shareData.query_results?.data
      if (!hasResults) {
        this.initLegacyStaticReport(query, shareData)
        return
      }
      this.isRollingReport = shareData.type === 'updating'
      this.initShareReport(query, shareData) // Nov 6 2025 (onwards): We updated the payload to include the query_results data
    },

    // New generic report init function (used for static and rolling reports) - used for new share reports that provide query results based on the fetch payload
    initShareReport (query, shareData) {
      const queryResults = shareData.query_results?.data
      const fetchPayload = query.fetch_payload
      // selected_columns in fetch payload actually refer to table columns (dont ask why)
      const tableColumns = fetchPayload.selected_columns
      this.sharedReport = {
        ...shareData,
        graph: query.graph_type,
        tableConfig: query.table_config,
        tableColumns: tableColumns,
        summaryStats: shareData.query_results?.data?.summary_stats,
        sort: fetchPayload.metadata?.sort,
        timeseriesSnapshot: shareData.query_results?.timeseries_data,
        ...(this.isRollingReport ? { rollingDateRange: query?.rolling_date_range } : { staticDateRange: { start: new Date(query?.static_date_range.start), end: new Date(query?.static_date_range.end) } })
      }
      if (this.isRollingReport) {
        this.dateRange = parseDateRangeFromQuery([[{ key: 'event_date', operation: 'greater_than', value: query?.rolling_date_range }], [{ key: 'event_date', operation: 'less_than', value: 'today' }]])
      } else {
        this.dateRange = this.sharedReport?.staticDateRange
      }
      const metricsSnapshot = query.metricLookupSnapshot
      this.SET_METRIC_LOOKUP({ [this.getMetricsPlatform]: metricsSnapshot })
      this.lensCurrency = query?.currency
      this.lensId = this.sharedReport.lens_id
      this.selectedRows = queryResults?.ad_groups
      this.updateColorFormat(this.sharedReport?.tableConfig.colorFormat)
      this.updateRowColor({ keys: this.selectedRows, isAdding: true, graph: this.sharedReport.graph, ignorePrevColors: true })
      this.selectedColumns = query.selected_columns
      this.tableResults = queryResults ?? {}

      const { sums, meanAverages } = tableColumns.reduce((acc, kpi) => {
        if (kpi.key in queryResults) {
          acc.sums[kpi.key] = queryResults[kpi.key]
          acc.meanAverages[kpi.key] = queryResults.summary_stats?.ad_groups?.avg?.[kpi.key]
        }
        return acc
      }, { sums: {}, meanAverages: {} })

      this.netResults = { sums: sums, weighted_averages: queryResults?.weighted_averages, mean_averages: meanAverages, active_ad_count: queryResults?.active_ad_count, paused_ad_count: queryResults?.paused_ad_count, inactive_ad_count: queryResults?.inactive_ad_count }
    },

    // Legacy reports dont fetch data on backend, so we need to init the report manually here (used for static reports)
    initLegacyStaticReport (query, shareData) {
      this.sharedReport = {
        ...shareData,
        timeseriesSnapshot: query.timeseries_snapshot,
        graph: query.graph_type,
        sort: query.sorted_column,
        selectedColumns: query.selected_columns,
        tableConfig: query.table_config,
        tableColumns: query.table_columns,
        summaryStats: query.summary_stats,
        dateRange: {
          start: new Date(query?.date_range.start),
          end: new Date(query?.date_range.end)
        }
      }
      const metricsSnapshot = query.metricLookupSnapshot
      this.SET_METRIC_LOOKUP({ [this.getMetricsPlatform]: metricsSnapshot })
      this.lensId = this.sharedReport.lens_id
      this.lensCurrency = query?.currency || 'USD'

      this.updateColorFormat(this.sharedReport?.tableConfig.colorFormat)
      this.selectedRows = query.snapshot
      this.updateRowColor({ keys: this.selectedRows, isAdding: true, graph: this.sharedReport.graph, ignorePrevColors: true })
      this.selectedColumns = this.sharedReport?.selectedColumns
      this.tableResults = { ad_groups: query.snapshot }
      this.netResults = { sums: query.net_sums, weighted_averages: query.weighted_averages, mean_averages: query.mean_averages, active_ad_count: query.active_ad_count, paused_ad_count: query.paused_ad_count, inactive_ad_count: query.inactive_ad_count }
      console.log('selectedColumns-legacy', this.netResults)
    },

    // ========================== MISC METHODS ============================== \\

    sortTableResults (sort) {
      this.sharedReport.sort = sort
      const { by, order } = sort
      this.tableResults.ad_groups.sort((a, b) => {
        if (a[by] < b[by]) return order === 'asc' ? -1 : 1
        if (a[by] > b[by]) return order === 'asc' ? 1 : -1
        return 0
      })
    },
    pinColumn (newCols) {
      this.sharedReport.tableColumns = newCols
    }
  }
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.fadeHeader-enter-active,
.fadeHeader-leave-active {
  transition: all 0.2s ease-out;
  opacity: 1;
}

.fadeHeader-enter,
.fadeHeader-leave-to {
  opacity: 0;
}

.tableFade-enter-active,
.tableFade-leave-active {
  transition: all 0.25s ease-out;
}

.tableFade-enter-from {
  opacity: 0;
  transform: translateY(1rem);
}

.tableFade-leave-to {
  opacity: 0;
  transform: translateY(-1rem);
}
</style>
